/**
 * Phase 20 Slice 2 — JSON exporter for `SimulationResult`.
 *
 * Originally landed in `packages/app/src/lib/exportSim.ts`. Phase 21
 * Slice 1A (refactor): the JSON-shaped pieces moved here so the
 * headless CLI can produce byte-identical output to a UI-driven
 * export without depending on `packages/app` (which would drag React,
 * Vite, and the DOM into the CLI binary).
 *
 * The split lines are mechanical:
 *   - JSON shape (`toJson`, `fromJson`, `SimExport`, `ParsedSimExport`,
 *     `SIM_EXPORT_VERSION`) is engine-side — pure functions over
 *     `SimulationResult` plus run/project metadata as primitives.
 *   - CSV outputs, filename builder, and the SimRun/ProjectFile
 *     adapter stay in `packages/app/src/lib/exportSim.ts`.
 *
 * The .json artefact is the canonical export — every field of
 * `SimulationResult` is round-trippable, with `Date` fields
 * serialised as ISO strings and parsed back by `fromJson`.
 */
/** Schema version bumped only when a backwards-incompatible field changes. */
export const SIM_EXPORT_VERSION = 1;
// ── Functions ────────────────────────────────────────────────────────────────
function nameOf(map, id) {
    return map[id] ?? id;
}
export function toJson(input) {
    const { result: r, nodeNames: names } = input;
    const result = {
        endDates: r.endDates.map((d) => d.toISOString()),
        percentiles: {
            p50: r.percentiles.p50.toISOString(),
            p80: r.percentiles.p80.toISOString(),
            p95: r.percentiles.p95.toISOString(),
        },
        criticalityIndex: r.criticalityIndex,
        tornado: r.tornado.map((t) => ({
            nodeId: t.nodeId,
            name: nameOf(names, t.nodeId),
            impactHours: t.impactHours,
        })),
        convergence: r.convergence,
        pathFrequency: r.pathFrequency.map((p) => ({
            ids: p.path,
            names: p.path.map((id) => nameOf(names, id)),
            count: p.count,
        })),
        pathPerIteration: r.pathPerIteration,
        nodeP95: Object.fromEntries(Object.entries(r.nodeP95).map(([id, d]) => [id, d.toISOString()])),
        projectCosts: r.projectCosts,
        costPercentiles: r.costPercentiles,
        nodeCostStats: r.nodeCostStats,
        costTornado: r.costTornado.map((t) => ({
            nodeId: t.nodeId,
            name: nameOf(names, t.nodeId),
            impactCost: t.impactCost,
        })),
        costCurve: r.costCurve,
    };
    const out = {
        kind: 'caladia.sim',
        version: SIM_EXPORT_VERSION,
        exportedAt: input.exportedAt.toISOString(),
        projectName: input.projectName,
        currency: input.currency,
        fxSnapshotVersion: input.fxSnapshotVersion,
        ...(input.fxRateOverrides && Object.keys(input.fxRateOverrides).length > 0
            ? { fxRateOverrides: input.fxRateOverrides }
            : {}),
        iterations: input.iterations,
        seed: input.seed,
        ...(input.excludes && input.excludes.length > 0 ? { excludes: input.excludes } : {}),
        runId: input.runId,
        runTimestamp: input.runTimestamp.toISOString(),
        target: input.target,
        result,
    };
    return JSON.stringify(out, null, 2);
}
/**
 * Parse a JSON export back into a typed shape, converting ISO date
 * strings to real `Date` instances. Used by the round-trip test.
 *
 * Permissive on shape — trusts the input came from `toJson` and does
 * not validate every field. If we want a hardened import (load an
 * external export back into the app), add a Zod schema.
 */
export function fromJson(text) {
    const raw = JSON.parse(text);
    const r = raw.result;
    return {
        ...raw,
        result: {
            ...r,
            endDates: r.endDates.map((s) => new Date(s)),
            percentiles: {
                p50: new Date(r.percentiles.p50),
                p80: new Date(r.percentiles.p80),
                p95: new Date(r.percentiles.p95),
            },
            nodeP95: Object.fromEntries(Object.entries(r.nodeP95).map(([id, s]) => [id, new Date(s)])),
        },
    };
}
//# sourceMappingURL=export.js.map